import base_url from "./baseUrl";
import {
  getUserToken,
  getUserRefreshToken,
  logoutUser,
  notifyUser,
} from "./util";
import secureLocalStorage from "react-secure-storage";

// Track ongoing refresh to prevent multiple simultaneous refreshes
let isRefreshing = false;
let failedQueue = [];

// A global error tracker to ensure we notify only once per error
const notifiedErrors = new Set();

// Helper function to check if a route requires authentication
const requiresAuthentication = (param) => {
  const UNAUTHENTICATED_ROUTES = [
    'login',
    'verify-email',
    'verify-otp',
    'create-password',
    'register',
    'forgot-password',
    'reset-password',
    'resend-verification',
    'help/contact',
    'help/support'
  ];
  return !UNAUTHENTICATED_ROUTES.some(route => param.includes(route));
};

// Helper function to notify only once for the same error
const notifyOnce = (type, message, title = "") => {
  if (!notifiedErrors.has(message)) {
    notifiedErrors.add(message);
    notifyUser(type, message, title);
  }
};

// Process queued requests after refreshing token
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Refresh the access token
const refreshAccessToken = () => {
  return new Promise((resolve, reject) => {
    const refreshToken = getUserRefreshToken();
    const token = getUserToken();

    if (!refreshToken) {
      notifyOnce("error", "No refresh token found. Please log in again.", "Error");
      reject("No refresh token found");
      logoutUser();
      return;
    }

    if (isRefreshing) {
      failedQueue.push({ resolve, reject });
      return;
    }

    isRefreshing = true;

    fetch(`${base_url}token/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ tokenValue: refreshToken }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.success) {
          secureLocalStorage.setItem("token", data.data.accessToken);
          secureLocalStorage.setItem("refreshToken", data.data.refreshToken);
          
          processQueue(null, data.data.accessToken);
          resolve(data.data.accessToken);
        } else {
          const error = data?.message || "Refresh token failed";
          processQueue(error, null);
          reject(error);
          logoutUser();
        }
      })
      .catch((error) => {
        notifyOnce("error", "Failed to refresh the access token. Please log in again.", "Error");
        processQueue(error, null);
        reject(error);
        logoutUser();
      })
      .finally(() => {
        isRefreshing = false;
      });
  });
};

// Server request with Bearer token
export let serverRequest = (type, param, data, otpRequired = false, otp = null) => {
  let URL = base_url;

  return new Promise((resolve, reject) => {
    const token = getUserToken();
    const needsAuth = requiresAuthentication(param);

    const headers = {
      Accept: "application/json",
    };

    // Only set Content-Type if not FormData
    if (!(data instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    if (token && needsAuth) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (otpRequired && otp) {
      headers.otp = otp;
    }

    const makeRequest = (accessToken = null) => {
      const requestHeaders = { ...headers };
      if (accessToken && needsAuth) {
        requestHeaders.Authorization = `Bearer ${accessToken}`;
      }

            const fullUrl = URL.endsWith("/") && param.startsWith("/")
        ? URL + param.slice(1)
        : !URL.endsWith("/") && !param.startsWith("/")
        ? URL + "/" + param
        : URL + param;
      return fetch(fullUrl, {
        method: type,
        headers: requestHeaders,
        body: data instanceof FormData ? data : (data === '' ? undefined : JSON.stringify(data)),
      });
    };

    makeRequest(token)
      .then((response) => response.json())
      .then((resp) => {
        if (needsAuth && resp?.code === "401" && resp?.message === "Token expired, please login again") {
          const refreshToken = getUserRefreshToken();

          if (!refreshToken) {
            notifyOnce("error", resp?.message);
            logoutUser();
            reject(resp);
            return;
          }

          if (isRefreshing) {
            failedQueue.push({
              resolve: (newToken) => {
                makeRequest(newToken)
                  .then(response => response.json())
                  .then(resolve)
                  .catch(reject);
              },
              reject
            });
            return;
          }

          refreshAccessToken()
            .then((newAccessToken) => {
              return makeRequest(newAccessToken);
            })
            .then((retryResponse) => retryResponse.json())
            .then((retryResp) => resolve(retryResp))
            .catch((retryError) => {
              notifyOnce("error", "An error occurred. Please try again later.", "Error");
              reject(retryError);
            });
        } else if (resp?.code === "401" && needsAuth) {
          notifyOnce("error", resp?.message);
          logoutUser();
          reject(resp);
        } else if (resp?.code === "401" && !needsAuth) {
          notifyOnce("error", resp?.message);
          reject(resp);
        } else {
          resolve(resp);
        }
      })
      .catch((error) => {
        notifyOnce("error", "An error occurred. Please try again later.", "Error");
        reject(error);
      });
  });
};

// Server request with FormData and no Bearer token
export let serverRequest2 = (type, param, formData, otpRequired = false, otp = null) => {
  let URL = base_url;

  return new Promise((resolve, reject) => {
    const token = getUserToken();
    const needsAuth = requiresAuthentication(param);

    const headers = {
      Accept: "application/json",
    };

    if (token && needsAuth) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (otpRequired && otp) {
      headers.otp = otp;
    }

    const makeRequest = (accessToken = null) => {
      const requestHeaders = { ...headers };
      if (accessToken && needsAuth) {
        requestHeaders.Authorization = `Bearer ${accessToken}`;
      }

            const fullUrl = URL.endsWith("/") && param.startsWith("/")
        ? URL + param.slice(1)
        : !URL.endsWith("/") && !param.startsWith("/")
        ? URL + "/" + param
        : URL + param;
      return fetch(fullUrl, {
        method: type,
        headers: requestHeaders,
        body: formData,
      });
    };

    makeRequest(token)
      .then(async (response) => {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          return response.json();
        }
        return response.text();
      })
      .then((resp) => {
        if (needsAuth && resp?.code === "401" && resp?.message === "Token expired, please login again") {
          const refreshToken = getUserRefreshToken();

          if (!refreshToken) {
            notifyOnce("error", resp?.message);
            logoutUser();
            reject(resp);
            return;
          }

          if (isRefreshing) {
            failedQueue.push({
              resolve: (newToken) => {
                makeRequest(newToken)
                  .then(async response => {
                    const contentType = response.headers.get("content-type");
                    if (contentType && contentType.includes("application/json")) {
                      return response.json();
                    }
                    return response.text();
                  })
                  .then(resolve)
                  .catch(reject);
              },
              reject
            });
            return;
          }

          refreshAccessToken()
            .then((newAccessToken) => {
              return makeRequest(newAccessToken);
            })
            .then(async (retryResponse) => {
              const contentType = retryResponse.headers.get("content-type");
              if (contentType && contentType.includes("application/json")) {
                return retryResponse.json();
              }
              return retryResponse.text();
            })
            .then((retryResp) => resolve(retryResp))
            .catch((retryError) => {
              notifyOnce("error", "An error occurred. Please try again later.", "Error");
              reject(retryError);
            });
        } else if (resp?.code === "401" && needsAuth) {
          notifyOnce("error", resp?.message);
          logoutUser();
          reject(resp);
        } else if (resp?.code === "401" && !needsAuth) {
          notifyOnce("error", resp?.message);
          reject(resp);
        } else {
          resolve(resp);
        }
      })
      .catch((error) => {
        notifyOnce("error", "An error occurred. Please try again later.", "Error");
        reject(error);
      });
  });
};

// Server request with no Bearer token
export let serverRequestNoBearer = (type, param, data) => {
  let URL = base_url;

  return new Promise((resolve, reject) => {
    const token = getUserToken();
    
          const fullUrl = URL.endsWith("/") && param.startsWith("/")
        ? URL + param.slice(1)
        : !URL.endsWith("/") && !param.startsWith("/")
        ? URL + "/" + param
        : URL + param;
    fetch(fullUrl, {
      method: type,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
        Accept: "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((resp) => resolve(resp))
      .catch((error) => {
        notifyOnce("error", "An error occurred. Please try again later.", "Error");
        reject(error);
      });
  });
};
